/**
 * How a video is played on the client.
 *
 * A video record can be one of two things, and they need different players:
 *
 *  - A Cloudflare Stream asset — `cloudflareVideoId` is a 32-character hex uid.
 *    It plays through the Stream embed (`iframeUrl`).
 *  - A plain media file — what the backend produces when Cloudflare Stream is
 *    disabled (`ENABLE_CLOUDFLARE_STREAM` unset) and what any self-hosted upload
 *    looks like. Its id is a UUID, which the Stream embed rejects with
 *    "no video id or valid token found in path". It plays in a `<video>` tag.
 *
 * The API resolves this server-side and sends `playbackKind` + the matching URL
 * (see `backend/src/utils/videoPlayback.ts`). This mirror exists for the admin
 * endpoints that still return raw video documents, and as a fallback for a
 * response predating `playbackKind`. Nothing outside this module should build a
 * playback URL by hand.
 */

/** A Cloudflare Stream uid — 32 hex characters, no dashes. */
const STREAM_UID = /^[0-9a-f]{32}$/i;

/** Stream uid embedded in a stored playback URL. */
const STREAM_UID_IN_URL = /(?:videodelivery\.net|cloudflarestream\.com)\/([0-9a-f]{32})/i;

export type VideoPlaybackKind = "stream" | "file" | "none";

export interface VideoPlayback {
  kind: VideoPlaybackKind;
  /** Cloudflare Stream embed. Empty unless `kind` is "stream". */
  iframeUrl: string;
  /** Direct media URL for a `<video>` element. Empty unless `kind` is "file". */
  fileUrl: string;
}

export const NO_PLAYBACK: VideoPlayback = { kind: "none", iframeUrl: "", fileUrl: "" };

/** The playback-relevant fields any video-shaped API payload may carry. */
export interface PlayableVideo {
  playbackKind?: VideoPlaybackKind | string | null;
  iframeUrl?: string | null;
  playbackUrl?: string | null;
  cloudflareVideoId?: string | null;
  /** Paid add-on videos come back flagged when the viewer has not bought them. */
  locked?: boolean | null;
}

/**
 * Works out how to play a video, or `none` when it cannot be played — which is
 * also the answer for a locked add-on, since the server withholds its URLs.
 */
export function resolveVideoPlayback(video?: PlayableVideo | null): VideoPlayback {
  if (!video || video.locked) return NO_PLAYBACK;

  const iframeUrl = String(video.iframeUrl || "").trim();
  const playbackUrl = String(video.playbackUrl || "").trim();
  const id = String(video.cloudflareVideoId || "").trim();

  // Trust the server when it has already classified the video.
  if (video.playbackKind === "stream" && iframeUrl) {
    return { kind: "stream", iframeUrl, fileUrl: "" };
  }
  if (video.playbackKind === "file" && playbackUrl) {
    return { kind: "file", iframeUrl: "", fileUrl: playbackUrl };
  }
  if (video.playbackKind === "none") return NO_PLAYBACK;

  // Otherwise classify it the same way the server would.
  const uid = STREAM_UID.test(id) ? id : playbackUrl.match(STREAM_UID_IN_URL)?.[1] || "";
  if (uid) {
    return {
      kind: "stream",
      iframeUrl: iframeUrl || `https://iframe.videodelivery.net/${uid}`,
      fileUrl: "",
    };
  }

  if (/^https?:\/\//i.test(playbackUrl)) {
    return { kind: "file", iframeUrl: "", fileUrl: playbackUrl };
  }

  return NO_PLAYBACK;
}

/** True when the video can actually be played right now. */
export const isPlayable = (video?: PlayableVideo | null): boolean =>
  resolveVideoPlayback(video).kind !== "none";
